import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, renderTitle, requireImageUrl, brandInitials, PHOTO_GRADE } from "./types";

// venueScript — "editorial wedding hero". Full-bleed graded photo with the
// brand wordmark centered at the TOP and a duo-headline anchored at the
// BOTTOM: a wide-tracked uppercase serif line with a gold SCRIPT word tucked
// beneath it, then a spaced three-word tagline. Modeled on the classic
// venue-poster look (e.g. "RUSTIC / Elegance / Beautiful · Relaxed ·
// Unforgettable"). The most decorative of the layouts — best on a hero shot
// (a couple, the venue exterior) where the center of the frame is open.
//   brandLogo / brand → top-center wordmark (or monogram)
//   venue.name        → the big uppercase serif line
//   titleAccent       → the gold script word tucked under it (optional)
//   venue.tagline     → the spaced tagline at the very bottom (optional)
const GOLD_HI = "#e7d1a0"; // gradient top — light champagne
const GOLD_LO = "#c39a4f"; // gradient bottom — warm gold

export function venueScriptTemplate(input: PinInput): string {
  const { brand, brandLogo, typography, venue } = input;
  const imageUrl = requireImageUrl(input, "venueScript");
  if (!venue) {
    throw new Error("venueScript template requires venue { name, location?, tagline? }");
  }

  // The big uppercase serif word/line. Short concepts ("Rustic") read huge;
  // longer hooks step down and wrap to two lines.
  const caps = venue.name.toUpperCase();
  const capLen = caps.length;
  const capSize = capLen <= 8 ? 138 : capLen <= 14 ? 108 : capLen <= 22 ? 82 : 64;
  const capTrack = capLen <= 14 ? 14 : 8; // letter-spacing px (tighter when long)

  // Accent handling, two cases:
  //  • DISTINCT word (own-brand: caps "RUSTIC" + script "Elegance") → render it
  //    as the big gold script word tucked under the caps (the classic look).
  //  • Part of the headline (client pins, where the accent is inline emphasis)
  //    → render it INLINE in gold script within the caps, so the flourish still
  //    shows but the word isn't duplicated.
  const accentRaw = (input.titleAccent && input.titleAccent.trim()) || "";
  const accentInline = !!accentRaw && caps.toLowerCase().includes(accentRaw.toLowerCase());
  const capsHtml = accentInline ? renderTitle(venue.name, accentRaw) : escapeHtml(caps);
  const script = accentInline ? "" : accentRaw;
  const scriptLen = script.length;
  const scriptSize = scriptLen <= 10 ? 150 : scriptLen <= 18 ? 118 : 92;

  // No-logo monogram fallback (top-center): custom text or auto initials.
  const monoText = (input.monogramText && input.monogramText.trim()) || brandInitials(brand);
  // NOTE: class names must NOT be "script"/"serif" — the bottom accent word uses
  // a global `.script` rule (gold background-clip:text) that would otherwise also
  // hit the monogram and clip its ornate flourishes. Use mono-* names.
  const monoClass = input.monogramFont === "serif" ? "mono-serif" : "mono-script";
  // Size the monogram to its letter count so 3-letter initials (e.g. "CBW")
  // don't overflow the ring or jumble in the connected script.
  const monoLen = monoText.length;
  const ringSize = monoLen <= 2 ? 128 : 150;
  const ringFont = monoLen <= 2 ? 54 : monoLen <= 3 ? 40 : 32;
  const ringTrack = monoLen <= 2 ? 2 : 0;
  const scriptFont = monoLen <= 2 ? 104 : monoLen <= 3 ? 82 : 66;
  const scriptTrack = monoLen >= 3 ? 6 : 0; // separate the connected caps

  // Logo sits over the (darkened) top of the photo → whiten unless it's a
  // full-color mark the whitening would flatten.
  const logoFilter = input.logoFullColor
    ? "drop-shadow(0 2px 10px rgba(0,0,0,.55))"
    : "brightness(0) invert(1) drop-shadow(0 2px 10px rgba(0,0,0,.55))";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#000}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;color:#fff}
  .image{position:absolute;inset:0;background-image:url('${cssUrl(imageUrl)}');background-size:cover;background-position:${Math.round((input.focusX ?? 0.5) * 100)}% ${Math.round((input.focusY ?? 0.5) * 100)}%;filter:${PHOTO_GRADE}}
  /* Soft darken at the very top (for the wordmark) and a deeper foot (for the
     headline) — the middle of the frame stays bright. */
  .image::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.46) 0%,rgba(0,0,0,0) 16%),linear-gradient(0deg,rgba(0,0,0,.78) 0%,rgba(0,0,0,.40) 20%,rgba(0,0,0,0) 44%)}
  /* Top-center wordmark. */
  .mark{position:absolute;top:58px;left:60px;right:60px;z-index:2;display:flex;align-items:center;justify-content:center}
  .logo{max-height:244px;max-width:100%;width:auto;height:auto;display:block;filter:${logoFilter}}
  .monogram{display:flex;align-items:center;justify-content:center;text-shadow:0 2px 10px rgba(0,0,0,.55)}
  .monogram.mono-serif{width:${ringSize}px;height:${ringSize}px;border:2.5px solid rgba(255,255,255,.9);border-radius:50%;font-family:${typography.headlineFont};font-size:${ringFont}px;letter-spacing:${ringTrack}px;color:#fff}
  /* Solid gold (no background-clip:text → ornate flourishes never clip). Roomy
     line-height + padding give Great Vibes' tall swashes space top & bottom. */
  .monogram.mono-script{font-family:'Great Vibes',cursive;font-size:${scriptFont}px;line-height:1.35;padding:.12em .18em;letter-spacing:${scriptTrack}px;color:${GOLD_LO}}
  /* Bottom headline block, centered. */
  .hero{position:absolute;left:60px;right:60px;bottom:96px;z-index:2;text-align:center}
  .caps{font-family:${typography.headlineFont};font-weight:${typography.headlineWeight};font-size:${capSize}px;line-height:1.12;
    letter-spacing:${capTrack}px;text-transform:uppercase;padding-bottom:.08em;text-shadow:0 3px 20px rgba(0,0,0,.55)}
  /* Inline gold script accent inside the headline (client pins) — solid gold,
     no background-clip, so the flourish never clips. */
  /* Accent gets its OWN line (block) so the title stacks: caps / script / caps. */
  .caps .accent{display:block;font-family:'Great Vibes',cursive;font-style:normal;text-transform:none;color:${GOLD_LO};font-weight:400;letter-spacing:0;font-size:2.85em;line-height:.9;margin:.02em 0}
  /* Gold script word, tucked up so it overlaps the serif line's baseline.
     Great Vibes has deep swash descenders (g/y/j) — line-height + padding give
     the tails room so they're never clipped. */
  .script{font-family:'Great Vibes',cursive;font-size:${scriptSize}px;line-height:1.45;margin-top:-.62em;padding:.30em 0 .36em;
    background:linear-gradient(180deg,${GOLD_HI} 0%,${GOLD_LO} 100%);-webkit-background-clip:text;background-clip:text;color:transparent;
    filter:drop-shadow(0 3px 14px rgba(0,0,0,.5))}
  /* Spaced tagline (e.g. "Beautiful  Relaxed  Unforgettable"). */
  .tagline{margin-top:26px;font-size:30px;letter-spacing:7px;word-spacing:10px;text-transform:uppercase;font-weight:600;opacity:.96;text-shadow:0 2px 10px rgba(0,0,0,.5)}
</style></head>
<body><div class="pin">
  <div class="image"></div>
  <div class="mark">
    ${
      input.hideLogo
        ? ""
        : brandLogo
          ? `<img class="logo" src="${brandLogo}" alt="${escapeHtml(brand)}" />`
          : `<span class="monogram ${monoClass}">${escapeHtml(monoText)}</span>`
    }
  </div>
  <div class="hero">
    <div class="caps">${capsHtml}</div>
    ${script ? `<div class="script">${escapeHtml(script)}</div>` : ""}
    ${venue.tagline ? `<div class="tagline">${escapeHtml(venue.tagline)}</div>` : ""}
  </div>
</div></body></html>`;
}
